import * as R from 'ramda';
import { Endpoints } from '../../../../core/api/endpoints';
import { ApiReducer, makeApiReducer, makeApiAction, makeRsaaTypes } from '../../../../core/api/rsaa';
import { addError, noLoadingNoError, addLoading, getLoading } from '../../../../core/api/module';
import { ActionType } from '../../../../core/redux/actionType';
import { DevPlanFieldAndAnswer } from '../../models';
import { BENCHMARKS } from '../../../../language/time';


const KEY_ACTION = ActionType.AddDevPlanActionRequest;
const ACTIONS    = [
  ActionType.AddDevPlanActionRequest,
  ActionType.AddDevPlanActionResponse,
  ActionType.AddDevPlanActionError,
];


type Payload = DevPlanFieldAndAnswer[];
type Meta = { projectId: number; devPlanId: number; };


/**
 * Dispatch this action to add a new dev plan item action
 */
export const addDevPlanActionAction = (projectId: number, devPlanId: number) => makeApiAction<Payload, Meta>({
  method:   'POST',
  endpoint: Endpoints.AddDevPlanAction,
  headers:  { 'content-type': 'application/json' },
  body:     state => JSON.stringify({
    devItemId: devPlanId,
    languageId: state.locale.lang.languageId,
    projectId: projectId ?? 0,
    participantId: state.auth.currentUser?.personId ?? 0,
  }),
  types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { projectId, devPlanId }),
  bailout:  state => !!getLoading(state, KEY_ACTION),
});


const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  if (isLoading) {
    return addLoading(state, KEY_ACTION);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg);
  }

  const successState = noLoadingNoError(state, KEY_ACTION);

  const actionId = (payload || [])[0]?.devActionId ?? -1;
  const itemId   = action.meta.devPlanId;

  const actions = (payload || [])
    .map(fa => !!fa.devPlanFieldId ? fa : R.assoc('devPlanFieldId', fa.question, fa));

  return R.assocPath(
    ['devPlan', 'fieldsAnswers', `${itemId}`, `${actionId}`],
    actions,
    R.assocPath(
      ['devPlan', 'actionsByItemId', `${itemId}`],
      R.concat(
        [
          {
            devItemId:   itemId,
            devActionId: actionId,
            goalDate:    BENCHMARKS.Y2K,
            status:      0,
          },
        ],
        successState.devPlan?.actionsByItemId?.[`${itemId}`] || [],
      ),
      successState,
    ),
  );
};


export const addDevPlanActionReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
